<template>
  <div
    class="flex min-h-screen min-w-[300px] flex-col antialiased"
    :style="{
      minHeight: store.pageHeight > 0 ? `${store.pageHeight}px` : 'auto',
    }"
  >
    <a
      href="#main-content"
      class="focus:ring-blue-500 sr-only absolute left-0 top-0 z-50 m-2 rounded bg-white p-2 text-black shadow-md transition duration-300 focus:not-sr-only focus:outline-none focus:ring-2"
    >
      {{ $t('global.skip_to_main_content') }}
    </a>
    <HeaderBenefitsBanner />
    <div class="z-0 overflow-x-hidden sm:sticky sm:top-0 sm:z-50">
      <Intersect
        :threshold="0.5"
        @enter.once="handleVisibility(bannerData.content)"
      >
        <Banner
          v-if="bannerData && !isCheckout"
          :blok="{
            ...bannerData.content,
            published_at: bannerData.published_at || '',
          }"
        />
      </Intersect>
    </div>
    <ClientOnly>
      <FAB v-if="!isCheckoutPage" />
    </ClientOnly>

    <Header
      ref="header"
      :fetching-categories="rootCategoriesPending"
      :root-categories="navigationCategories"
      :blok="navigationData?.content"
      :force-minified="isCheckout"
      :stick-to-banner="!isMobile && !isCheckout"
    >
      <div
        v-if="!(isMobile && isCheckoutPage && showCheckoutPageNewLayout)"
        class="relative z-40"
        :class="{
          '!absolute inset-x-0': isPdp && isMobile,
          'pointer-events-none': !isMobileSearchBarVisible,
        }"
      >
        <HeaderSearch
          class="bg-white px-3 pb-2 transition-all md:hidden"
          :class="{
            '!-translate-y-full opacity-0': !isMobileSearchBarVisible,
          }"
          width="full"
          @change:search-results-visibility="
            handleMobileSearchResultsVisibilityChange
          "
        />
      </div>
    </Header>

    <ToastContainer />

    <LazyMobileNavigation
      v-if="isMobileOrTablet && isSideNavigationOpen"
      :fetching="rootCategoriesPending"
      :root-categories="navigationCategories"
      :blok="cmsMainNavigationDataContent"
    />
    <div id="main-content" class="container z-10 grow bg-white xl:mt-2">
      <AuthErrorToastContainer />
      <slot
        keep-alive
        :keep-alive-props="{ include: ['index', 'product-slug'] }"
      />
      <ClientOnly>
        <LanguageSwitchModal />
      </ClientOnly>
      <div class="backdrop-target" />
    </div>
    <div class="modal-target" />
    <NuxtLazyHydrate when-visible>
      <Footer
        v-if="footerData?.content"
        class="mt-12"
        :hide-benefits="isBlog"
        :blok="footerData.content"
      />
    </NuxtLazyHydrate>
    <div class="filter-slide-in-target" />
    <div class="selection-modal-target" />
    <BackInStockModal v-if="showBackInStock" />
    <AddToBasketPopover v-if="isAddToBasketFlyoutOpen" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useEventListener, useDebounceFn } from '@vueuse/core'
import { useHead } from '#imports'
import useExperiments from '~/composables/useExperiments'
import { useCurrentShop } from '#storefront/composables'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { fetchOnNavigation } from '#layers/base/utils/loading'
import useGlobalConfig from '~/composables/useGlobalConfig'
import useAgsWishlist from '~/composables/useAgsWishlist'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import useAgsBasket from '~/composables/useAgsBasket'
import useRootCategories from '#layers/base/composables/useRootCategories'
import { useMainNavigationCategories } from '#layers/base/composables/useMainNavigationCategories'
import useCms from '~/composables/cms/useCms'
import useZoomGalleryActions from '#layers/base/composables/ui/useZoomGalleryActions'
import { useDeviceType } from '~/composables/useDeviceType'
import { useRouter, useRoute } from '#app/composables/router'
import { useNuxtApp } from '#app/nuxt'
import useStore from '#layers/base/composables/useStore'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import useUiState from '~/composables/ui/useUiState'
import useExperimentTracking from '#layers/base/composables/useExperimentTracking'
import useParameterMemory from '#layers/base/composables/useParameterMemory'
import type {
  SbBanner,
  SbFooter,
  SbNavigation,
} from '~/cms/storyblok/types/storyblok.gen'
import addUserCentricsScript from '#layers/base/composables/helpers/userCentrics'
import { useUsercentricsSettingsIds } from '#layers/base/composables/useUsercentricsSettingsIds'
import { buildUsercentricsLookupKey } from '#layers/base/utils/usercentricsConfig'
import useBackInStock from '~/composables/useBackInStock'
import useAgsUser from '#layers/base/composables/useAgsUser'
import { usePromotionController } from '#layers/base/composables/usePromotionController'
import useDefaultLayoutTracking from '#layers/base/composables/useDefaultLayoutTracking'
import useCatchAllPrefetch from '#layers/base/composables/useCatchAllPrefetch'
import { generateOrganizationSchema } from '~/utils/seo'

useParameterMemory('utm_')
useExperimentTracking()

const {
  isSideNavigationOpen,
  selectSideNavigationEntry,
  isProductSizeGuideModalOpen,
  isShippingHintCostModalOpen,
  isMobileSearchBarVisible,
  isPdp,
  showBackInStock,
  isAddToBasketFlyoutOpen,
} = useUiState()

useBackInStock()

const { toLocalePath } = useRouteHelpers()
const { isoLanguageCode, country } = useCurrentShop().value
const fullLocale = `${isoLanguageCode}-${country === 'UK' ? 'GB' : country}`
// Usercentrics settings IDs come from a server-derived useState payload, NOT
// from useCurrentShop(). The shop config bakes process.env at build time and
// silently produces undefined IDs in Docker builds. runtimeConfig.public is
// frozen in Nuxt 4 so it can't be populated from a Nitro plugin either; the
// useState factory runs server-side after Kamal env vars are loaded and the
// value is auto-serialised into the SSR payload. See
// layers/base/composables/useUsercentricsSettingsIds.ts and
// docs/solutions/build-deploy/process-env-build-time-bake-in-nuxt-static-config.md.
const usercentricsSettingsIds = useUsercentricsSettingsIds()
const usercentricsSettingsId =
  usercentricsSettingsIds.value[
    buildUsercentricsLookupKey({ isoLanguageCode, country })
  ] || ''
const store = useStore()
const { $i18n } = useNuxtApp()
const config = useRuntimeConfig()

const route = useRoute()
const router = useRouter()
const gtmId = config.public.gtm.id

// Other components (TopCategories, ScrollableLinkList, useNewPlp …) also call
// `useRootCategories()` during render. In Nuxt 4, each new call without cached
// data temporarily flips `status` back to `'pending'`, which would re-gate
// `HeaderSubNavigation`'s `v-if="!fetching"` and hide the main-nav items even
// though `rootCategories.value.categories` is already populated. Treat the
// nav as ready as soon as we have data — the in-flight re-fetch doesn't
// invalidate what we already rendered.
const rootCategoriesPending = computed(
  () =>
    rootCategoriesStatus.value === 'pending' &&
    !rootCategories.value?.categories?.length,
)

const { showCheckoutPageNewLayout, loadLeapScript } = useExperiments()
const isCheckoutPage = computed(() => route.fullPath.includes('checkout'))

let lastScrollTop = 0

function hideOrShowSearchBar() {
  if (isPdp.value && isMobile.value) {
    return
  }
  const scrollTop = document?.documentElement?.scrollTop
  if (typeof scrollTop !== 'number') {
    return
  }
  const diff = Math.abs(scrollTop - lastScrollTop)
  if (diff <= 10 && scrollTop > 100) {
    return
  }
  isMobileSearchBarVisible.value = scrollTop < 100 || scrollTop <= lastScrollTop
  lastScrollTop = scrollTop
}

useEventListener('scroll', useDebounceFn(hideOrShowSearchBar, 10))

watch(
  () => route.fullPath,
  async () => {
    // On navigation, scroll restoration can happen after this watcher fires.
    // Recompute after the next frame(s) so the mobile search bar doesn't get stuck hidden.
    await nextTick()
    lastScrollTop = 0
    requestAnimationFrame(() => {
      hideOrShowSearchBar()
      requestAnimationFrame(hideOrShowSearchBar)
    })
  },
)

onMounted(() => {
  if (usercentricsSettingsId) {
    addUserCentricsScript(isoLanguageCode, usercentricsSettingsId)
  }
  hideOrShowSearchBar()
})

const { isMobileOrTablet, isMobile } = useDeviceType()
const isCheckout = computed(() => route.name?.toString().includes('checkout'))
const isBlog = computed(() =>
  route.path.includes(toLocalePath({ name: 'magazine' }).toString()),
)

const areMobileSearchResultsVisible = ref(false)
const { state: zoomGallery } = useZoomGalleryActions()
const handleMobileSearchResultsVisibilityChange = (areVisible: boolean) => {
  areMobileSearchResultsVisible.value = areVisible
}

const { trackPromotionView } = useTrackingEvents()

// CMS
const { fetchBySlug: fetchBanner, data: bannerData } =
  useCms<SbBanner>('banner')
const { fetchBySlug: fetchFooter, data: footerData } =
  useCms<SbFooter>('footer')
const { fetchBySlug: fetchNavigation, data: navigationData } =
  useCms<SbNavigation>('main-navigation')

const { data: rootCategories, status: rootCategoriesStatus } =
  useRootCategories({
    params: { children: NAVIGATION_DEPTH },
  })

// Categories backing the main navigation: the root tree plus any on-demand
// subtrees for non-root categories referenced by a MainNavigation entry.
const { categories: navigationCategories } = useMainNavigationCategories(
  computed(() => navigationData.value?.content?.MainNavigation),
  computed(() => rootCategories.value?.categories),
)
const basket = useAgsBasket()
const { isLoggedIn, refresh } = useAgsUser()
const { fetch: fetchSmartShopConfiguration } = useSmartShopConfiguration()
const { fetch: fetchWishlist } = useAgsWishlist()
const {
  fetch: fetchGlobalConfig,
  runKameleoon,
  idKameleoon,
  pathKameleoon,
} = useGlobalConfig()
const { load: loadHtfScripts } = useHtfScripts()
const { prefetch: prefetchCatchAllData } = useCatchAllPrefetch()
usePromotionController({ setup: true })
useDefaultLayoutTracking(rootCategories)

const trackBannerPromotionView = () => {
  const banner = bannerData.value?.content

  if (isCheckout.value || !banner) {
    return
  }

  trackPromotionView({
    id: banner.promotion_id || 'banner_promotion_id',
    name: banner.promotion_name || 'banner_promotion_name',
    pageType: 'all',
    promoType: 'headerbanner',
    position: 1,
    creativeNameSb: banner.creative_name || '',
  })
}

onMounted(async () => {
  if (!isLoggedIn.value) {
    await refresh()
  }

  trackBannerPromotionView()
})

const fetchData = async () => {
  try {
    await Promise.all([
      fetchNavigation('main-navigation'),
      fetchBanner('banner'),
      fetchFooter('footer'),
      fetchSmartShopConfiguration(),
      fetchGlobalConfig(),
      // Prefetch catch-all page data (category/CMS) in parallel with layout data
      prefetchCatchAllData(),
      // `fetchCountryRedirect` is intentionally NOT called here. The country
      // lookup is owned by `LanguageSwitchModal.vue` so we don't bake any
      // per-IP data into the route-cached SSR payload (see
      // `utils/ssrCache/index.ts`) and so the modal can guarantee its own
      // freshness on initial hydration (where this layout's fire-and-forget
      // `fetchOnNavigation` would otherwise lose the result).
      ...(import.meta.client
        ? [fetchWishlist(), basket.fetch(), refresh()]
        : []),
    ])
  } catch (error) {
    console.error('Error fetching navigation:', error)
  }
}

const { trackShopInit } = useTrackingEvents()

trackShopInit()

const handleVisibility = (banner: SbBanner) => {
  trackPromotionView({
    id: banner.promotion_id || 'banner_promotion_id',
    name: banner.promotion_name || 'banner_promotion_name',
    pageType: 'all',
    promoType: 'headerbanner',
    position: 1,
    creativeNameSb: banner.creative_name || '',
  })
}

if (import.meta.client) {
  router.afterEach(() => {
    trackBannerPromotionView()

    // reset side navigation when leaving a product page or PLP
    if (
      !route.name?.toString().includes('all') &&
      !route.name?.toString().includes('p-slug-id')
    ) {
      selectSideNavigationEntry(undefined)
    }
  })
}
// END TRACKING

if (import.meta.client && !sessionStorage.getItem('referrer')) {
  sessionStorage.setItem('referrer', document.referrer)
}

const cmsMainNavigationDataContent = computed(
  () => navigationData.value?.content,
)

await fetchOnNavigation(fetchData)

loadHtfScripts()

useJsonld(() =>
  generateOrganizationSchema({
    organizationName: $i18n.t('sd.organisation.name'),
    organizationDescription: $i18n.t('sd.organisation.description'),
    organizationLogo: $i18n.t('sd.organisation.logo'),
    organisationImage: $i18n.t('sd.organisation.image'),
    organisationParentName: $i18n.t('sd.organisation.parent.name'),
    organizationParentUrl: $i18n.t('sd.organisation.parent.url'),
    locale: country,
  }),
)

useHead(() => ({
  titleTemplate(title) {
    return title
      ? $i18n.t('global.title_template', { title })
      : $i18n.t('global.title_default')
  },
  script: [
    ...(config.public.gtm.source && config.public.gtm.id
      ? [
          {
            id: 'gtm-inline-script',
            innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        '${config.public.gtm.source}?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${config.public.gtm.id}');`,
            type: 'text/javascript',
            tagPriority: 'high' as const,
            charset: 'utf-8',
          },
        ]
      : []),
    ...(loadLeapScript.value && runKameleoon.value
      ? [
          {
            innerHTML: `
                  // Duration in milliseconds to wait while the Kameleoon application file is loaded
                  var kameleoonLoadingTimeout = 1000;
                  window.kameleoonQueue = window.kameleoonQueue || [];
                  window.kameleoonStartLoadTime = new Date().getTime();
                  if (!document.getElementById("kameleoonLoadingStyleSheet") && !window.kameleoonDisplayPageTimeOut) {
                    var kameleoonS = document.getElementsByTagName("script")[0];
                    var kameleoonCc = "* { visibility: hidden !important; background-image: none !important; }";
                    var kameleoonStn = document.createElement("style");
                    kameleoonStn.type = "text/css";
                    kameleoonStn.id = "kameleoonLoadingStyleSheet";
                    if (kameleoonStn.styleSheet) {
                      kameleoonStn.styleSheet.cssText = kameleoonCc;
                    } else {
                      kameleoonStn.appendChild(document.createTextNode(kameleoonCc));
                    }
                    kameleoonS.parentNode.insertBefore(kameleoonStn, kameleoonS);
                    window.kameleoonDisplayPage = function(fromEngine) {
                      if (!fromEngine) {
                        window.kameleoonTimeout = true;
                      }
                      if (kameleoonStn.parentNode) {
                        kameleoonStn.parentNode.removeChild(kameleoonStn);
                      }
                    };
                    window.kameleoonDisplayPageTimeOut = window.setTimeout(window.kameleoonDisplayPage, kameleoonLoadingTimeout);
                  }
                `,
            type: 'text/javascript',
            tagPriority: 'high' as const,
          },
          {
            src: `//${idKameleoon.value}.kameleoon.io/${pathKameleoon.value}`,
            async: true,
            tagPriority: 'low' as const,
            fetchpriority: 'high' as const,
          },
        ]
      : []),
  ],
  noscript: gtmId
    ? [
        {
          id: 'gtm-noscript',
          innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
          tagPosition: 'bodyOpen' as const,
        },
      ]
    : [],
  __dangerouslyDisableSanitizersByTagID: {
    'gtm-inline-script': ['innerHTML'],
    'gtm-noscript': ['innerHTML'],
  },
  bodyAttrs: {
    /** HIDE SCROLLBAR WHILE MODALS OR NAVIGATION SLIDE ARE OPEN */
    class:
      showBackInStock.value ||
      isSideNavigationOpen.value ||
      isProductSizeGuideModalOpen.value ||
      isShippingHintCostModalOpen.value ||
      zoomGallery?.display
        ? 'overflow-hidden'
        : '',
  },
  htmlAttrs: {
    lang: fullLocale,
  },
  link: [
    { rel: 'preconnect', href: '//a.storyblok.com' },
    { rel: 'preconnect', href: '//app.storyblok.com' },
    {
      rel: 'preconnect',
      href: '//api.usercentrics.eu',
    },
    {
      rel: 'preconnect',
      href: '//app.usercentrics.eu',
    },
    {
      rel: 'dns-prefetch',
      href: '//api.usercentrics.eu',
    },
    { rel: 'dns-prefetch', href: '//app.usercentrics.eu' },
    {
      rel: 'preload',
      href: 'https://web.cmp.usercentrics.eu/ui/loader.js',
      as: 'script',
    },
  ],
  meta: [
    ...(config.public.googleSiteVerification
      ? [
          {
            name: 'google-site-verification',
            content: config.public.googleSiteVerification,
          },
        ]
      : []),
  ],
}))
</script>
